import { forwardRef, useMemo, useState } from "react";

// eslint-disable-next-line no-restricted-imports
import { Box, ButtonBase, CircularProgress, SxProps, Theme, useTheme } from "@mui/material";

import { NextLink } from "../../NextLink/NextLink";

import { getRootSx, getSizeSx, getFilledSx, getTonalSx, getOutlinedSx, getTextSx, ICON_SIZE_MAP } from "./styles";
import { DashboardButtonProps, FilledButtonVibe, OtherButtonVibe } from "./types";

import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { mergeSx } from "@/helpers/mergeSx";

const InnerDashboardButton = forwardRef<HTMLButtonElement, DashboardButtonProps>(
  (
    {
      children,
      disabled,
      endIcon,
      fullWidth = false,
      inline = false,
      isLoading: isLoadingProp = false,
      slotProps,
      size = "medium",
      startIcon,
      variant = "text",
      vibe = "brand",
      onClick,
      sx,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const [internalLoading, setInternalLoading] = useState(false);
    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        setInternalLoading(true);
        await onClick(e);
      }
      setInternalLoading(false);
    };

    const isLoading = isLoadingProp || internalLoading;

    const circularProgressSize = theme.icon.size.default[size === "large" ? "small" : "extraSmall"];
    const iconSize = ICON_SIZE_MAP[size];

    const baseStyles: SxProps<Theme> = {
      ...getRootSx(theme),
      ...getSizeSx(theme, size),
      ...((disabled || isLoading) && { opacity: 0.4 }),
      ...(fullWidth && { width: "100%" }),
      ...(isLoading && {
        position: "relative",
      }),
    } as SxProps<Theme>;

    const variantStyles: SxProps<Theme> = useMemo(() => {
      switch (variant) {
        case "filled":
          return getFilledSx(theme, vibe as FilledButtonVibe);
        case "tonal":
          return getTonalSx(theme, vibe as OtherButtonVibe);
        case "outlined":
          return getOutlinedSx(theme, vibe as OtherButtonVibe);
        case "text":
          return getTextSx(theme, vibe as OtherButtonVibe);
      }
    }, [variant, vibe, theme]);

    // type-check breaks if we merge all styles inline, break it up into separate variables
    const styles: SxProps<Theme> = mergeSx(baseStyles, variantStyles, sx);

    return (
      <ButtonBase
        ref={ref}
        onClick={handleClick}
        disabled={isLoading || disabled}
        data-test={rest["data-test"] || "button"}
        disableRipple
        sx={styles}
        {...rest}
      >
        {isLoading && (
          <CircularProgress size={circularProgressSize} sx={{ position: "absolute", color: "currentColor" }} />
        )}
        {startIcon && (
          <DashboardIcon
            vibe="currentColor"
            inline={inline}
            size={iconSize}
            sx={mergeSx({ ...(isLoading && { visibility: "hidden" }) }, slotProps?.startIcon?.sx)}
          >
            {startIcon!}
          </DashboardIcon>
        )}
        <Box sx={mergeSx({ ...(isLoading && { visibility: "hidden" }) }, slotProps?.content?.sx)}>{children}</Box>
        {endIcon && (
          <DashboardIcon
            vibe="currentColor"
            inline={inline}
            size={iconSize}
            sx={mergeSx({ ...(isLoading && { visibility: "hidden" }) }, slotProps?.endIcon?.sx)}
          >
            {endIcon!}
          </DashboardIcon>
        )}
      </ButtonBase>
    );
  }
);

InnerDashboardButton.displayName = "InnerDashboardButton";

export const DashboardButton = forwardRef<HTMLButtonElement, DashboardButtonProps>((props, ref) => {
  const { href, target, ...rest } = props;
  if (href && target !== "_blank") {
    return (
      <NextLink href={href} target={target} locale={false}>
        <InnerDashboardButton {...rest} ref={ref} />
      </NextLink>
    );
  } else {
    return <InnerDashboardButton href={href} target={target} {...rest} ref={ref} />;
  }
});

DashboardButton.displayName = "DashboardButton";
